import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Plus, X, Trash2, ShoppingBasket, Loader2, ChevronDown, ChevronUp, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useBetSlip } from "../context/BetSlipContext";
import { useAuth } from "../context/AuthContext";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { downloadNodeAsPng } from "../lib/slipToPng";

/**
 * Floating "Bet Slip" basket widget. Fixed to bottom-right of every page.
 *
 * Flow:
 *   1. User clicks "+" on any prediction card → pick lands here
 *   2. Widget shows count badge + stake input
 *   3. Checkout button: POST /api/betway/parlay then polls until terminal
 *   4. On placed: confirms + clears slip; on match_not_found / skipped:
 *      reports which legs were removed from the parlay
 */
export const BetSlipDrawer = () => {
  const { picks, removePick, clear, drawerOpen, setDrawerOpen } = useBetSlip();
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const [stake, setStake] = useState("1.00");
  const [placing, setPlacing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const captureRef = useRef(null);
  const [accountLinked, setAccountLinked] = useState(null);
  const [betStatus, setBetStatus] = useState(null);
  // Per-pick Betway availability (true / false / null)
  const [pickAvailability, setPickAvailability] = useState({});
  // Per-pick 1X2 decimal odds map  ({pickId: {home, draw, away, href} | null})
  const [pickOdds, setPickOdds] = useState({});

  // Whenever picks change, batch-check Betway availability
  useEffect(() => {
    if (picks.length === 0) {
      setPickAvailability({});
      setPickOdds({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const matches = picks.map((p) => ({
          home_team: p.home_team,
          away_team: p.away_team,
          league: p.league,
          country: p.country,
        }));
        const [{ data: avail }, { data: odds }] = await Promise.all([
          axios.post(`${API}/betway/availability/check`, { matches }),
          axios.post(`${API}/betway/availability/odds`, { matches }),
        ]);
        if (cancelled) return;
        const availMap = {};
        const oddsMap = {};
        (avail?.results || []).forEach((val, i) => {
          const id = picks[i]?.id;
          if (id) availMap[id] = val;
        });
        (odds?.results || []).forEach((val, i) => {
          const id = picks[i]?.id;
          if (id) oddsMap[id] = val;
        });
        setPickAvailability(availMap);
        setPickOdds(oddsMap);
      } catch (_) {
        // Silent — leave previous map
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [picks]);

  // List of pick ids that Betway explicitly does NOT have right now
  const unavailableIds = useMemo(
    () => picks.filter((p) => pickAvailability[p.id] === false).map((p) => p.id),
    [picks, pickAvailability]
  );

  // Decide which 1X2 side a pick refers to so we can pull the right
  // decimal odd from the cache.  Returns null for non-1X2 markets
  // (BTTS / Over-Under / Correct Score / etc.) — those legs show "—"
  // in the UI and are excluded from the parlay payout multiplication.
  const pickToSide = (p) => {
    const mt = (p.market_type || "").toLowerCase();
    const pr = (p.prediction || "").toLowerCase().trim();
    const home = (p.home_team || "").toLowerCase();
    const away = (p.away_team || "").toLowerCase();
    // Only the match-result family carries usable 1X2 odds.
    if (
      !mt.includes("match result") &&
      !mt.includes("1x2") &&
      !mt.includes("full time result") &&
      mt !== "result"
    ) {
      return null;
    }
    if (pr === "draw" || pr === "x") return "draw";
    if (pr === "home" || pr === "1") return "home";
    if (pr === "away" || pr === "2") return "away";
    if (home && (pr === home || pr.includes(home))) return "home";
    if (away && (pr === away || pr.includes(away))) return "away";
    return null;
  };

  // Decimal odds for each pick (or null when no 1X2 odds available)
  const pickDecimalOdds = useMemo(() => {
    const out = {};
    for (const p of picks) {
      const cached = pickOdds[p.id];
      const side = pickToSide(p);
      if (cached && side && cached[side] != null) {
        out[p.id] = cached[side];
      } else {
        out[p.id] = null;
      }
    }
    return out;
  }, [picks, pickOdds]);

  // Parlay decimal odds = ∏ of leg odds.  Legs without odds are
  // multiplied as 1.0 so they don't zero out the payout — but the UI
  // shows a "missing odds" warning so the user understands.
  const combinedDecimal = useMemo(() => {
    let prod = 1;
    let knownLegs = 0;
    for (const p of picks) {
      const o = pickDecimalOdds[p.id];
      if (o && o > 1) {
        prod *= o;
        knownLegs += 1;
      }
    }
    return { decimal: prod, knownLegs };
  }, [picks, pickDecimalOdds]);

  const stakeNum = parseFloat(stake) || 0;
  const potentialPayout = stakeNum * combinedDecimal.decimal;
  const potentialProfit = potentialPayout - stakeNum;
  const allLegsHaveOdds =
    picks.length > 0 && combinedDecimal.knownLegs === picks.length;

  const removeUnavailable = () => {
    if (unavailableIds.length === 0) return;
    unavailableIds.forEach((id) => removePick(id));
    toast.success(
      `Removed ${unavailableIds.length} pick${unavailableIds.length === 1 ? "" : "s"} not on Betway`,
      { duration: 2500 }
    );
  };

  // Picks whose kickoff is more than 18 hours away — Betway CA
  // typically only publishes lines ~18-24h before kickoff, so these
  // legs will silently get skipped at placement time.  We show a
  // warning + a one-click "Drop them" button.
  const earlyPicks = useMemo(() => {
    const now = Date.now();
    const HOURS_18 = 18 * 60 * 60 * 1000;
    return picks.filter((p) => {
      if (!p.kickoff) return false;
      const ko = new Date(p.kickoff).getTime();
      if (isNaN(ko)) return false;
      return ko - now > HOURS_18;
    });
  }, [picks]);

  const dropEarlyPicks = () => {
    earlyPicks.forEach((p) => removePick(p.id));
    toast.success(`Removed ${earlyPicks.length} pick${earlyPicks.length === 1 ? "" : "s"} that kick off in >18 h`);
  };

  useEffect(() => {
    if (!user) {
      setAccountLinked(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/betway/account`);
        if (!cancelled) setAccountLinked(!!r.data.linked);
      } catch {
        if (!cancelled) setAccountLinked(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const placeBet = async () => {
    if (picks.length === 0) {
      toast.error("Slip is empty — tap + on any pick to add it.");
      return;
    }
    if (!user) {
      toast.error("Please sign in first.");
      navigate("/auth");
      return;
    }
    if (accountLinked === false) {
      toast.error("Link your Betway account first (/account).");
      navigate("/account");
      return;
    }
    const amount = parseFloat(stake);
    if (!amount || amount < 0.5) {
      toast.error("Enter a valid stake (min $0.50).");
      return;
    }

    setPlacing(true);
    setBetStatus({ stage: "queued", message: "Submitting parlay…" });
    try {
      // Betway regulates in-play and pre-match into SEPARATE parlays in
      // most regions — split the slip so each half gets placed cleanly.
      const livePicks = picks.filter((p) => p.is_live);
      const preMatchPicks = picks.filter((p) => !p.is_live);
      const groups = [];
      if (preMatchPicks.length > 0) {
        groups.push({ label: "pre-match", picks: preMatchPicks, live: false });
      }
      if (livePicks.length > 0) {
        groups.push({ label: "live", picks: livePicks, live: true });
      }
      if (groups.length > 1) {
        toast.info(
          `Auto-splitting into ${groups.length} parlays (live + pre-match can't combine).`,
          { duration: 4500 }
        );
      }

      const placeOne = async (group) => {
        const payload = {
          stake: amount,
          action: "place",
          accepted_disclaimer: true,
          is_live: group.live,
          selections: group.picks.map((p) => ({
            match_id: String(p.fixture_id),
            home_team: p.home_team,
            away_team: p.away_team,
            market_label: p.market_type,
            pick: p.prediction,
            odds: p.odds ?? null,
            confidence: p.confidence ?? null,
            league: p.league ?? null,
            country: p.country ?? null,
            is_live: !!p.is_live,
          })),
        };
        const { data } = await axios.post(`${API}/betway/parlay`, payload);
        const betId = data.bet_id;
        // Poll until terminal
        const deadline = Date.now() + 200 * 1000;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 2000));
          const { data: doc } = await axios.get(`${API}/betway/parlay/${betId}`);
          setBetStatus({
            stage: doc.stage,
            status: doc.status,
            message: `[${group.label}] ${doc.message || ""}`,
            skipped_selections: doc.skipped_selections || [],
          });
          if (["placed", "failed"].includes(doc.status)) {
            return { group, doc };
          }
        }
        return { group, doc: { status: "failed", message: "Timed out" } };
      };

      // Run groups serially so the log + UI clearly show each stage.
      const results = [];
      for (const g of groups) {
        const r = await placeOne(g);
        results.push(r);
      }

      const allOk = results.every((r) => r.doc.status === "placed");
      if (allOk) {
        const bits = results.map((r) => {
          const skipN = (r.doc.skipped_selections || []).length;
          const placedN = r.group.picks.length - skipN;
          return `${placedN}/${r.group.picks.length} ${r.group.label}`;
        });
        toast.success(
          `Bet placed on Betway · $${amount} · ${bits.join(" + ")}`,
          { duration: 6500 }
        );
        clear();
      } else {
        const failed = results.find((r) => r.doc.status !== "placed");
        toast.error(
          failed ? `[${failed.group.label}] ${failed.doc.message || "failed"}` :
          "Bet placement failed.",
          { duration: 8000 }
        );
      }
    } catch (e) {
      let msg = e?.response?.data?.detail ?? e?.message ?? "Placement error";
      if (Array.isArray(msg)) {
        // FastAPI 422 validation errors → list of {loc, msg, type} objects
        msg = msg.map((d) => d?.msg || JSON.stringify(d)).join(" · ");
      } else if (typeof msg !== "string") {
        msg = JSON.stringify(msg);
      }
      toast.error(msg);
    } finally {
      setPlacing(false);
    }
  };

  if (picks.length === 0 && !drawerOpen) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 max-w-[92vw] sm:max-w-sm"
      data-testid="betslip-drawer"
    >
      {!drawerOpen ? (
        <button
          onClick={() => setDrawerOpen(true)}
          className="group relative flex items-center gap-2 px-4 py-3 rounded-full bg-foreground text-background shadow-2xl hover:scale-105 transition-transform"
          data-testid="betslip-open-btn"
        >
          <ShoppingBasket className="w-4 h-4" />
          <span className="font-display font-semibold text-sm">Bet Slip</span>
          <Badge
            variant="secondary"
            className="ml-1 bg-background text-foreground border-background font-bold"
            data-testid="betslip-count"
          >
            {picks.length}
          </Badge>
        </button>
      ) : (
        <div
          className="nd-card shadow-2xl border-2 border-foreground/20 w-[360px] sm:w-[400px] max-h-[80vh] flex flex-col"
          ref={captureRef}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <ShoppingBasket className="w-4 h-4" />
              <span className="font-display font-bold text-sm">Bet Slip</span>
              <Badge variant="outline" className="ml-1 text-[11px]">
                {picks.length} {picks.length === 1 ? "pick" : "picks"}
              </Badge>
            </div>
            <div className="flex items-center gap-1" data-png-skip="1">
              {picks.length > 0 && (
                <button
                  onClick={async () => {
                    if (downloading) return;
                    setDownloading(true);
                    const ok = await downloadNodeAsPng(captureRef.current, "ndakubet-betslip");
                    setDownloading(false);
                    if (ok) toast.success("Saved as PNG");
                    else toast.error("Couldn't render PNG. Try again.");
                  }}
                  className="p-1.5 rounded-md hover:bg-muted text-muted-foreground disabled:opacity-50"
                  title="Download as PNG"
                  disabled={downloading}
                  data-testid="betslip-download-png"
                >
                  {downloading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Download className="w-3.5 h-3.5" />
                  )}
                </button>
              )}
              {picks.length > 0 && (
                <button
                  onClick={clear}
                  className="p-1.5 rounded-md hover:bg-muted text-muted-foreground"
                  title="Clear all"
                  data-testid="betslip-clear"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-1.5 rounded-md hover:bg-muted text-muted-foreground"
                data-testid="betslip-close"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Picks list */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
            {/* Auto-remove unavailable banner */}
            {unavailableIds.length > 0 && (
              <div
                className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 flex items-start gap-2"
                data-testid="betslip-unavailable-banner"
              >
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-foreground">
                    {unavailableIds.length} pick{unavailableIds.length === 1 ? "" : "s"} not on Betway right now
                  </div>
                  <button
                    onClick={removeUnavailable}
                    className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold tracking-[0.04em] uppercase rounded-full px-2.5 py-1 bg-foreground text-background hover:bg-foreground/90"
                    data-testid="betslip-remove-unavailable"
                  >
                    <X className="w-3 h-3" /> Remove unavailable
                  </button>
                </div>
              </div>
            )}

            {picks.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-10">
                Tap <span className="font-bold text-foreground">+</span> on any pick above to add it.
              </div>
            ) : (
              picks.map((p) => {
                const avail = pickAvailability[p.id];
                const onBetway = avail === true;
                const offBetway = avail === false;
                return (
                <div
                  key={p.id}
                  className={`rounded-lg border px-3 py-2.5 text-xs ${
                    offBetway
                      ? "border-amber-500/40 bg-amber-500/5"
                      : "border-border bg-subtle"
                  }`}
                  data-testid={`betslip-pick-${p.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-foreground truncate flex items-center gap-1.5">
                        {p.is_live && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-[0.06em] bg-destructive/15 text-destructive border border-destructive/40"
                            data-testid={`betslip-live-badge-${p.id}`}
                          >
                            <span className="w-1 h-1 rounded-full bg-destructive animate-pulse" />
                            LIVE
                          </span>
                        )}
                        <span className="truncate">{p.home_team} vs {p.away_team}</span>
                      </div>
                      <div className="text-muted-foreground text-[11px] mt-0.5 truncate">
                        {p.market_type} · <span className="text-foreground">{p.prediction}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-[10px] font-bold text-foreground tabular-nums">
                          {p.confidence}%
                        </span>
                        {p.risk_level && (
                          <span className="text-[10px] text-muted-foreground">
                            · {p.risk_level} risk
                          </span>
                        )}
                        {pickDecimalOdds[p.id] != null && (
                          <span
                            className="text-[10px] font-bold tabular-nums text-emerald-700 dark:text-emerald-400"
                            data-testid={`betslip-odds-${p.id}`}
                            title="Decimal odds from Betway CA"
                          >
                            @ {pickDecimalOdds[p.id].toFixed(2)}
                          </span>
                        )}
                        {pickToSide(p) != null && pickDecimalOdds[p.id] == null && (
                          <span className="text-[10px] text-muted-foreground">
                            @ —
                          </span>
                        )}
                        {onBetway && (
                          <span className="text-[9px] font-bold tracking-[0.04em] uppercase text-emerald-600 dark:text-emerald-400">
                            ✓ On Betway
                          </span>
                        )}
                        {offBetway && (
                          <span className="text-[9px] font-bold tracking-[0.04em] uppercase text-amber-600 dark:text-amber-400">
                            — Not on Betway yet
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => removePick(p.id)}
                      className="shrink-0 p-1 rounded hover:bg-muted text-muted-foreground hover:text-red-500"
                      data-testid={`betslip-remove-${p.id}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                );
              })
            )}
          </div>

          {/* Status bubble */}
          {betStatus && (
            <div className="px-4 py-2 border-t border-border bg-subtle text-[11px] text-muted-foreground">
              <span className="font-semibold text-foreground capitalize">
                {typeof betStatus.stage === "string" ? betStatus.stage : "status"}
              </span>
              {betStatus.message && (
                <span>
                  {" · "}
                  {String(
                    typeof betStatus.message === "string"
                      ? betStatus.message
                      : Array.isArray(betStatus.message)
                      ? betStatus.message.map((d) => d?.msg || JSON.stringify(d)).join(", ")
                      : JSON.stringify(betStatus.message)
                  ).slice(0, 120)}
                </span>
              )}
              {Array.isArray(betStatus.skipped_selections) &&
                betStatus.skipped_selections.length > 0 && (
                  <div className="mt-1 text-[10px]">
                    Removed (not on Betway):{" "}
                    {betStatus.skipped_selections
                      .map((s) => `${s?.home_team || "?"} vs ${s?.away_team || "?"}`)
                      .join(", ")}
                  </div>
                )}
            </div>
          )}

          {/* Footer — stake + place */}
          <div className="px-4 py-3 border-t border-border space-y-2">
            {earlyPicks.length > 0 && (
              <div
                className="rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-[11px] flex items-start gap-2"
                data-testid="betslip-early-warning"
              >
                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="flex-1 leading-snug">
                  <span className="font-semibold text-amber-700 dark:text-amber-300">
                    {earlyPicks.length} of {picks.length} pick{earlyPicks.length === 1 ? "" : "s"} kick off in 18+ hours
                  </span>
                  <p className="text-muted-foreground mt-0.5">
                    Betway CA typically publishes lines ~18&ndash;24h before kickoff.
                    These legs will likely be <strong>skipped</strong> at placement time.
                  </p>
                  <button
                    type="button"
                    onClick={dropEarlyPicks}
                    className="mt-1.5 text-amber-700 dark:text-amber-300 underline hover:no-underline font-semibold"
                    data-testid="betslip-drop-early-btn"
                  >
                    Drop them ({earlyPicks.length})
                  </button>
                </div>
              </div>
            )}
            {picks.length > 0 && (
              <div
                className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 text-[11px]"
                data-testid="betslip-payout-summary"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">Combined odds</span>
                  <span className="font-bold tabular-nums text-foreground">
                    {combinedDecimal.knownLegs > 0
                      ? combinedDecimal.decimal.toFixed(2)
                      : "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <span className="text-muted-foreground">
                    Potential payout
                    {!allLegsHaveOdds && combinedDecimal.knownLegs > 0 && (
                      <span className="ml-1 text-amber-600 dark:text-amber-400">
                        *
                      </span>
                    )}
                  </span>
                  <span
                    className="font-display font-bold text-base tabular-nums text-emerald-600 dark:text-emerald-400"
                    data-testid="betslip-potential-payout"
                  >
                    ${combinedDecimal.knownLegs > 0
                      ? potentialPayout.toFixed(2)
                      : "—"}
                  </span>
                </div>
                {combinedDecimal.knownLegs > 0 && (
                  <div className="flex items-center justify-between gap-2 mt-0.5 text-muted-foreground">
                    <span>Profit if it hits</span>
                    <span className="tabular-nums">
                      ${potentialProfit.toFixed(2)}
                    </span>
                  </div>
                )}
                {!allLegsHaveOdds && combinedDecimal.knownLegs > 0 && (
                  <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-1.5 leading-snug">
                    * Some legs use markets without 1X2 odds in cache (BTTS,
                    Over/Under, etc.) — those aren't included in the multiplier
                    above.  Final payout will reflect Betway's combined odds.
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold text-muted-foreground">Stake ($)</span>
              <Input
                type="number"
                min="0.5"
                step="0.5"
                value={stake}
                onChange={(e) => setStake(e.target.value)}
                className="h-8 w-24 text-sm font-tabular"
                data-testid="betslip-stake"
                disabled={placing}
              />
            </div>
            <Button
              onClick={placeBet}
              disabled={placing || picks.length === 0}
              className="w-full bg-foreground text-background hover:bg-foreground/90 font-display font-bold"
              data-testid="betslip-checkout"
            >
              {placing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Placing on Betway…
                </>
              ) : accountLinked === false ? (
                "Link Betway Account →"
              ) : (
                `Place Parlay · $${(parseFloat(stake) || 0).toFixed(2)}`
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Compact "+"/"✓" toggle button to attach to each prediction pick card.
 */
export const AddToSlipButton = ({ pick, size = "sm" }) => {
  const { isPicked, togglePick } = useBetSlip();
  const picked = isPicked(pick.fixture_id, pick.market_type, pick.prediction);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        togglePick(pick);
        toast[picked ? "message" : "success"](
          picked ? "Removed from slip" : "Added to slip",
          { duration: 1500 }
        );
      }}
      className={`shrink-0 inline-flex items-center justify-center rounded-full border transition-colors
        ${picked
          ? "bg-foreground text-background border-foreground"
          : "bg-background text-foreground border-border hover:border-foreground"}
        ${size === "sm" ? "h-6 w-6" : "h-8 w-8"}`}
      data-testid={`add-to-slip-${pick.fixture_id}-${pick.market_type}-${pick.prediction}`.replace(/\s+/g, "-")}
      aria-label={picked ? "Remove from slip" : "Add to slip"}
      aria-pressed={picked}
    >
      {picked ? (
        <span className="text-xs font-bold">✓</span>
      ) : (
        <Plus className="w-3 h-3" />
      )}
    </button>
  );
};

export default BetSlipDrawer;
